import { useState, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { CardClient } from '@atlaskit/link-provider';

import {
	generateClickEventPayload,
	generateViewEventPayload,
	getClickAndViewEventObjectIdFromLinkData,
} from '@confluence/analytics/entry-points/companyHubAnalyticsHelpers';
import { useSessionData } from '@confluence/session-data';
import { useSpaceId } from '@confluence/space-utils';
import { getMonitoringClient } from '@confluence/monitoring';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';

/**
 * hook is used to create track event that captures user engagement with company hub landing page links
 * it is important to share the event payload between useLinkAnalyticActions and companyHubAnalyticsHandleEventCaught as this enforces SSOT
 * hook is different from companyHubAnalyticsHandleEventCaught, as useLinkAnalyticActions manually fetches resolved attributes from smart card client
 * for links that are not variants of atlaskit/smart-card.
 * This returns onClick fn that can be used by landing page links to fire companyHubLink clicked track event
 **/

type AnalyticActionsType = {
	handleClick?: () => void;
	handleView?: () => void;
};

type AnalyticFieldsType = {
	containerId?: string;
	tenantId?: string;
	objectId?: string;
};

export const useLinkAnalyticActions = (link: string): AnalyticActionsType => {
	const [analyticFields, setAnalyticFields] = useState<AnalyticFieldsType>();
	const spaceId = useSpaceId();
	const { activationId, cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		const fetchData = async () => {
			const client = new CardClient();

			try {
				const queriedMetadata = !!link && (await client.fetchData(link));

				if (!queriedMetadata || !queriedMetadata.meta) return;

				const {
					containerId: initialContainerId,
					tenantId: initialTenantId,
					objectId: initialObjectId,
				} = queriedMetadata.meta || {};

				if (!initialObjectId) {
					return;
				}

				const newObjectId = await getClickAndViewEventObjectIdFromLinkData(
					initialObjectId,
					initialContainerId,
				);

				setAnalyticFields({
					containerId: initialContainerId,
					tenantId: initialTenantId,
					objectId: newObjectId,
				});
			} catch (error) {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.COMPANY_HUB,
				});
				markErrorAsHandled(error);
			}
		};

		void fetchData();
	}, [link]);

	const { tenantId, objectId } = analyticFields || {};

	const handleClick = () => {
		if (tenantId === cloudId) {
			createAnalyticsEvent?.(
				generateClickEventPayload(activationId, cloudId, objectId, spaceId),
			).fire();
		}
	};

	const handleView = () => {
		if (tenantId === cloudId) {
			createAnalyticsEvent?.(
				generateViewEventPayload(activationId, cloudId, objectId, spaceId),
			).fire();
		}
	};

	return {
		handleClick: analyticFields ? handleClick : undefined,
		handleView: analyticFields ? handleView : undefined,
	};
};
